import { useState, useEffect } from "react";
import { Textarea } from "@/components/ui/textarea";
import { log } from "@/utils/debug";

interface ClinicianModeLayoutProps {
  text: string;
  onTextChange: (text: string) => void;
  eprType: "EPIC" | "Cerner" | "CareFlow";
}

export const ClinicianModeLayout = ({ text, onTextChange, eprType }: ClinicianModeLayoutProps) => {
  // Use a state to hold the text value
  const [localText, setLocalText] = useState(text);

  // Sync local state with parent state
  useEffect(() => {
    setLocalText(text);
  }, [text]);

  // Handle text changes
  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newText = e.target.value;
    setLocalText(newText);
    onTextChange(newText);

    // Log the text for debugging purposes, including line breaks
    log.process("Text changed in ClinicianModeLayout", {
      rawLength: newText.length,
      lineCount: (newText.match(/\n/g) || []).length + 1,
      hasLineBreaks: newText.includes("\n"),
      sample: newText.substring(0, 50) + (newText.length > 50 ? "..." : ""),
    });
  };

  const getHeaderTitle = () => {
    if (eprType === "EPIC") return "EPIC Progress Notes";
    if (eprType === "CareFlow") return "CareFlow Progress Notes";
    return "Cerner Clinical Notes";
  };

  const getHeaderColor = () => {
    if (eprType === "EPIC") return "text-teal-700";
    if (eprType === "CareFlow") return "text-teal-700";
    return "text-blue-700";
  };

  return (
    <div className="p-4 border rounded-md bg-white shadow-sm">
      <div className="mb-4">
        <h2 className={`text-lg font-medium ${getHeaderColor()}`}>{getHeaderTitle()}</h2>
        <p className="text-sm text-muted-foreground">Enter the clinical text below</p>
      </div>

      <Textarea
        value={localText}
        onChange={handleTextChange}
        placeholder="Enter clinical text here..."
        className="min-h-[300px] font-mono"
      />
    </div>
  );
};
